"use client";

import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { API_BASE_URL } from "@/lib/config";
import { useAuth } from "@/contexts/AuthContext";
import { authService } from "@/services/authService";
import UnlockOverlay from "@/components/data/UnlockOverlay";
import {
  aggregateRows,
  applyClientFilters,
  changedColumns,
  countActiveFilters,
  FLOW_DEFAULTS,
  paramsToScreenerBody,
  paramsToStreamQuery,
  rowPassesClientFilters,
  type FlowParams,
} from "@/lib/flow-filters";
import { FlowFilterSidebar, HoverTip } from "./FlowFilterSidebar";

// ── Types ────────────────────────────────────────────────────────────────────
type Row = Record<string, unknown>;

interface Props {
  initialParams: FlowParams;
  initial: {
    kind?: string;
    data?: Row[];
    columns?: string[];
    row_count?: number;
  } | null;
}

// ── Palette: monochrome black/white, hairline borders. Green/red carry
//    ONLY directional meaning; emerald is the single Xynth accent. ──────────
const C = {
  bg: "#000000",
  panel: "#060606",
  line: "rgba(255,255,255,0.09)",
  // Toolbar/control borders — clearly visible against black.
  lineStrong: "rgba(255,255,255,0.32)",
  lineSoft: "rgba(255,255,255,0.05)",
  text: "#f4f4f5",
  // "Dim" is still clearly readable white — no muddy gray anywhere.
  textDim: "rgba(255,255,255,0.72)",
  accent: "#10b981",
  accentDim: "rgba(16,185,129,0.10)",
  green: "#34d399",
  greenDim: "rgba(52,211,153,0.10)",
  red: "#f87171",
  redDim: "rgba(248,113,113,0.10)",
  gold: "#e7c66b",
};

// ── Deep logging: report client-side failures to the backend so "failed to
//    fetch" complaints are diagnosable from server logs. Fire-and-forget. ────
function logClient(event: string, meta: Record<string, unknown> = {}) {
  try {
    const body = JSON.stringify({
      event,
      page: "flow",
      url: typeof window !== "undefined" ? window.location.href : "",
      ua: typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 160) : "",
      ...meta,
    });
    // sendBeacon survives page unloads; fetch keepalive is the fallback.
    if (typeof navigator !== "undefined" && navigator.sendBeacon) {
      navigator.sendBeacon(
        `${API_BASE_URL}/api/data/client-log`,
        new Blob([body], { type: "application/json" })
      );
    } else {
      fetch(`${API_BASE_URL}/api/data/client-log`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
        keepalive: true,
      }).catch(() => {});
    }
  } catch {
    /* never let logging break the page */
  }
}

// ── Formatting helpers ────────────────────────────────────────────────────────
const num = (v: unknown): number | null => {
  if (v == null || v === "") return null;
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : null;
};

function fmtMoney(v: unknown): string {
  const n = num(v);
  if (n == null) return "—";
  const abs = Math.abs(n);
  if (abs >= 1e12) return `$${(n / 1e12).toFixed(2)}T`;
  if (abs >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `$${(n / 1e6).toFixed(2)}M`;
  if (abs >= 1e3) return `$${(n / 1e3).toFixed(1)}K`;
  return `$${n.toFixed(0)}`;
}

// UW's feed is inconsistent for marketcap: stocks come in raw dollars
// (AAPL → 3775801482090) but some ETFs come as billions (SPY → 691.58).
// Anything under 100k can't be a real dollar market cap, so treat it as $B.
function fmtMarketCap(v: unknown): string {
  const n = num(v);
  if (n == null || n === 0) return "—";
  return fmtMoney(n < 1e5 ? n * 1e9 : n);
}

function fmtNum(v: unknown): string {
  const n = num(v);
  if (n == null) return "—";
  return n.toLocaleString("en-US");
}

function fmtPrice(v: unknown): string {
  const n = num(v);
  if (n == null) return "—";
  return `$${n.toFixed(2)}`;
}

function fmtPct(v: unknown): string {
  const n = num(v);
  if (n == null) return "—";
  return `${(n * 100).toFixed(1)}%`;
}

function fmtGreek(v: unknown): string {
  const n = num(v);
  if (n == null) return "—";
  return n.toFixed(3);
}

// All feed timestamps are UTC. Display Eastern Time — the tape's native clock.
const ET_TIME = new Intl.DateTimeFormat("en-US", {
  timeZone: "America/New_York",
  hour12: false,
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
});
const ET_DATE = new Intl.DateTimeFormat("en-US", {
  timeZone: "America/New_York",
  year: "2-digit",
  month: "2-digit",
  day: "2-digit",
});

function parseUtc(v: unknown): Date | null {
  if (v == null || v === "") return null;
  if (typeof v === "number") {
    // Unix seconds vs milliseconds.
    return new Date(v < 1e11 ? v * 1000 : v);
  }
  let s = String(v);
  if (/^\d+$/.test(s)) {
    const n = Number(s);
    return new Date(n < 1e11 ? n * 1000 : n);
  }
  // Naive ISO strings from the feed are UTC — pin them so the browser
  // doesn't reinterpret in local time.
  if (!/[zZ]|[+-]\d{2}:?\d{2}$/.test(s)) s = `${s.replace(" ", "T")}Z`;
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
}

function fmtTimeET(v: unknown): string {
  const d = parseUtc(v);
  if (!d) return "—";
  return ET_TIME.format(d);
}

// Full date + time cell: "07/02/26 · 16:59:34" with the date slightly smaller
// but still WHITE (no gray dates anywhere on this page).
function TimeCell({ v }: { v: unknown }) {
  const d = parseUtc(v);
  if (!d) return <span style={{ color: C.text }}>—</span>;
  return (
    <span style={{ display: "inline-flex", alignItems: "baseline", gap: 6, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
      <span style={{ color: C.text, fontSize: 11 }}>{ET_DATE.format(d)}</span>
      <span style={{ color: C.text, fontWeight: 600 }}>{ET_TIME.format(d)}</span>
    </span>
  );
}

function fmtDate(v: unknown): string {
  if (!v) return "—";
  const s = String(v).slice(0, 10);
  const parts = s.split("-");
  if (parts.length === 3) return `${parts[1]}/${parts[2]}/${parts[0].slice(2)}`;
  return s;
}

function rowKey(r: Row, i: number): string {
  return (
    (r.id as string) ||
    `${r.ticker}-${r.strike}-${r.type}-${r.timestamp}-${r.size}-${i}`
  );
}

type CitedFilterChip = { key: string; label: string };

function describeFlowParam(key: string, value: string): string | null {
  if (!value) return null;
  const plain: Record<string, string> = {
    ticker: value,
    type: value === "call" ? "Calls only" : value === "put" ? "Puts only" : "",
    sides: `Side ${value.replace(/,/g, ", ")}`,
    sentiment: `Sentiment ${value}`,
    flag_types: `Execution ${value.replace(/,/g, ", ")}`,
    issue_types: `Type ${value.replace(/,/g, ", ")}`,
    sectors: `Sector ${value.replace(/,/g, ", ")}`,
    industries: `Industry ${value.replace(/,/g, ", ")}`,
    expiry_dates: `Expiry ${value.replace(/,/g, ", ")}`,
    tags: `Tags ${value.replace(/,/g, ", ")}`,
    excluded_tags: `Hidden tags ${value.replace(/,/g, ", ")}`,
    trade_codes: `Codes ${value.replace(/,/g, ", ")}`,
    exchanges: `Exchanges ${value.replace(/,/g, ", ")}`,
    earnings_week: value === "true" ? "Earnings this week" : "",
    unusual: value === "true" ? "Unusual only" : "",
    aggregated: value === "true" ? "Aggregated prints" : "",
    is_multi_leg: value === "true" ? "Multi-leg" : "",
    is_single_leg: value === "true" ? "Single-leg" : "",
    opening: value === "true" ? "Opening trades" : "",
    intraday_only: value === "true" ? "Today only" : "",
    show_canceled: value === "true" ? "Canceled shown" : "",
    vol_greater_oi: value === "true" ? "Vol > OI" : "",
    size_greater_oi: value === "true" ? "Size > OI" : "",
    is_otm: value === "true" ? "OTM only" : "",
    exclude_deep_itm: value === "true" ? "No deep ITM" : "",
    opex_only: value === "true" ? "Monthly OpEx" : "",
    hide_expired: value === "true" ? "No expired" : "",
    avoid_earnings: value === "true" ? "Avoid earnings" : "",
  };
  if (plain[key]) return plain[key];
  if (key.startsWith("min_")) return `${key.slice(4).replace(/_/g, " ")} >= ${value}`;
  if (key.startsWith("max_")) return `${key.slice(4).replace(/_/g, " ")} <= ${value}`;
  return null;
}

function citedFilterSummary(params: FlowParams): CitedFilterChip[] {
  const ignored = new Set(["limit", "order_by", "order_dir"]);
  const out: CitedFilterChip[] = [];
  for (const [key, value] of Object.entries(params)) {
    if (ignored.has(key)) continue;
    const label = describeFlowParam(key, value);
    if (label) out.push({ key, label });
  }
  return out.slice(0, 8);
}

// Bull vs bear from sentiment/side/tags.
function rowBull(r: Row): 1 | -1 | 0 {
  const s = String(r.sentiment || "").toLowerCase();
  if (s === "bullish") return 1;
  if (s === "bearish") return -1;
  const side = String(r.side || "").toLowerCase();
  const type = String(r.type || "").toLowerCase();
  if (side === "ask") return type === "put" ? -1 : 1;
  if (side === "bid") return type === "put" ? 1 : -1;
  return 0;
}

// ── Fill-vs-spread bar: where the fill sat between bid and ask ────────────────
function FillBar({ r }: { r: Row }) {
  const bid = num(r.bid);
  const ask = num(r.ask);
  const price = num(r.price);
  let pos = 0.5;
  if (bid != null && ask != null && price != null && ask > bid) {
    pos = Math.max(0, Math.min(1, (price - bid) / (ask - bid)));
  }
  const bull = rowBull(r);
  const color = bull > 0 ? C.green : bull < 0 ? C.red : C.text;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 120 }}>
      <span style={{ fontSize: 11, color: C.textDim, width: 36, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
        {bid != null ? bid.toFixed(2) : "—"}
      </span>
      <div
        style={{
          position: "relative",
          flex: 1,
          height: 2,
          background: C.line,
        }}
      >
        <div
          style={{
            position: "absolute",
            left: `calc(${pos * 100}% - 2px)`,
            top: -3,
            width: 4,
            height: 8,
            background: color,
          }}
        />
      </div>
      <span style={{ fontSize: 11, color: C.textDim, width: 36, fontVariantNumeric: "tabular-nums" }}>
        {ask != null ? ask.toFixed(2) : "—"}
      </span>
    </div>
  );
}

// ── Side badge (BID / MID / ASK) ─────────────────────────────────────────────
function SideBadge({ r }: { r: Row }) {
  const side = String(r.side || "").toUpperCase();
  if (!side || side === "NONE") return <span style={{ color: C.textDim }}>—</span>;
  const color = side === "ASK" ? C.green : side === "BID" ? C.red : C.gold;
  return (
    <span
      style={{
        fontSize: 10,
        fontWeight: 600,
        letterSpacing: 1,
        color,
        border: `1px solid ${color}`,
        padding: "1px 6px",
      }}
    >
      {side}
    </span>
  );
}

// ── Premium cell with magnitude bar ──────────────────────────────────────────
function PremiumCell({ r, maxPrem }: { r: Row; maxPrem: number }) {
  const p = num(r.premium) || 0;
  const bull = rowBull(r);
  const color = bull > 0 ? C.green : bull < 0 ? C.red : C.text;
  const w = maxPrem > 0 ? Math.max(3, (p / maxPrem) * 100) : 3;
  return (
    <div style={{ minWidth: 90 }}>
      <div style={{ fontVariantNumeric: "tabular-nums", color, fontWeight: 600 }}>
        {fmtMoney(p)}
      </div>
      <div style={{ height: 2, marginTop: 3, background: C.lineSoft }}>
        <div style={{ width: `${w}%`, height: "100%", background: color, opacity: 0.7 }} />
      </div>
    </div>
  );
}

// ── Vol vs OI cell ───────────────────────────────────────────────────────────
function VolOiCell({ r }: { r: Row }) {
  const vol = num(r.volume) || 0;
  const oi = num(r.open_interest) || 0;
  const ratio = num(r.vol_oi_ratio) ?? (oi > 0 ? vol / oi : 0);
  const hot = ratio >= 1;
  return (
    <div style={{ minWidth: 76 }}>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, fontVariantNumeric: "tabular-nums" }}>
        <span style={{ color: hot ? C.text : C.textDim, fontWeight: hot ? 600 : 400 }}>{fmtNum(vol)}</span>
        <span style={{ color: C.textDim }}>{fmtNum(oi)}</span>
      </div>
      <div style={{ height: 2, marginTop: 3, background: C.lineSoft }}>
        <div
          style={{
            width: `${Math.max(3, Math.min(100, ratio * 50))}%`,
            height: "100%",
            background: hot ? C.accent : "rgba(255,255,255,0.25)",
          }}
        />
      </div>
    </div>
  );
}

// ── Execution flags cell (SWEEP / CROSS / FLOOR / UNUSUAL / ×agg) ────────────
function FlagsCell({ r }: { r: Row }) {
  const badges: { label: string; color: string }[] = [];
  if (r.is_sweep) badges.push({ label: "SWEEP", color: C.gold });
  if (r.is_cross) badges.push({ label: "CROSS", color: C.text });
  if (r.is_floor) badges.push({ label: "FLOOR", color: C.text });
  if (r.unusual) badges.push({ label: "UNUSUAL", color: C.accent });
  if (r.canceled) badges.push({ label: "CANCELED", color: C.red });
  const aggN = num(r.agg_count);
  if (aggN != null && aggN > 1) badges.push({ label: `×${aggN}`, color: C.accent });
  if (!badges.length) return <span style={{ color: C.textDim }}>—</span>;
  return (
    <span style={{ display: "inline-flex", gap: 4 }}>
      {badges.map((b) => (
        <span
          key={b.label}
          style={{
            fontSize: 9,
            fontWeight: 700,
            letterSpacing: 0.8,
            color: b.color,
            border: `1px solid ${b.color}`,
            padding: "1px 5px",
            borderRadius: 3,
          }}
        >
          {b.label}
        </span>
      ))}
    </span>
  );
}

// ── Contract cell — UW style: strike · C/P · expiry in one column ────────────
function ContractCell({ r }: { r: Row }) {
  const t = String(r.type || "").toLowerCase();
  const call = t === "call";
  const strike = num(r.strike);
  return (
    <span style={{ display: "inline-flex", alignItems: "baseline", gap: 6, whiteSpace: "nowrap" }}>
      <span style={{ color: C.text, fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
        {strike != null ? `$${strike.toLocaleString("en-US")}` : "—"}
      </span>
      <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.8, color: call ? C.green : t === "put" ? C.red : C.textDim }}>
        {call ? "CALL" : t === "put" ? "PUT" : "—"}
      </span>
      <span style={{ color: C.text, fontSize: 11, fontVariantNumeric: "tabular-nums" }}>{fmtDate(r.expiry)}</span>
    </span>
  );
}

// ── Moneyness dot: ITM solid gold, ATM white, OTM hollow ─────────────────────
function MoneynessDot({ r }: { r: Row }) {
  const d = num(r.diff);
  if (d == null) return <span style={{ color: C.textDim }}>—</span>;
  const atm = Math.abs(d) < 0.005;
  const itm = d < 0;
  const label = atm ? "At the money" : itm ? `In the money (${(Math.abs(d) * 100).toFixed(1)}%)` : `Out of the money (${(d * 100).toFixed(1)}%)`;
  return (
    <span title={label} style={{ display: "inline-flex", justifyContent: "center", width: "100%" }}>
      <span
        style={{
          width: 8,
          height: 8,
          borderRadius: 999,
          background: atm ? C.text : itm ? C.gold : "transparent",
          border: `1.5px solid ${atm ? C.text : itm ? C.gold : "rgba(255,255,255,0.5)"}`,
        }}
      />
    </span>
  );
}

// ── Bid–Ask as text ($1.00 – $1.10) ───────────────────────────────────────────
function BidAskText({ r }: { r: Row }) {
  const bid = num(r.bid);
  const ask = num(r.ask);
  if (bid == null && ask == null) return <span style={{ color: C.textDim }}>—</span>;
  return (
    <span style={{ color: C.textDim, fontVariantNumeric: "tabular-nums", fontSize: 11.5 }}>
      {bid != null ? `$${bid.toFixed(2)}` : "—"}
      <span style={{ opacity: 0.6 }}> – </span>
      {ask != null ? `$${ask.toFixed(2)}` : "—"}
    </span>
  );
}

// ── Chain Bid/Ask: today's contract volume split bid vs ask (UW parity) ──────
function ChainBidAskBar({ r }: { r: Row }) {
  const askP = num(r.ask_perc);
  const bidP = num(r.bid_perc);
  if (askP == null && bidP == null) return <span style={{ color: C.textDim }}>—</span>;
  const a = Math.max(0, Math.min(1, askP || 0));
  const b = Math.max(0, Math.min(1, bidP || 0));
  const lead = a >= b ? a : b;
  const leadColor = a >= b ? C.green : C.red;
  return (
    <div style={{ minWidth: 84 }} title={`Ask-side ${(a * 100).toFixed(0)}% · Bid-side ${(b * 100).toFixed(0)}% of today's volume`}>
      <div style={{ display: "flex", height: 4, borderRadius: 2, overflow: "hidden", background: C.lineSoft }}>
        <div style={{ width: `${b * 100}%`, background: C.red, opacity: 0.85 }} />
        <div style={{ width: `${Math.max(0, 100 - b * 100 - a * 100)}%` }} />
        <div style={{ width: `${a * 100}%`, background: C.green, opacity: 0.85 }} />
      </div>
      <div style={{ fontSize: 10, marginTop: 2, color: leadColor, fontVariantNumeric: "tabular-nums", fontWeight: 600 }}>
        {(lead * 100).toFixed(0)}%
      </div>
    </div>
  );
}

// ── Share equivalent: delta × size × 100 ─────────────────────────────────────
function ShareEqvCell({ r }: { r: Row }) {
  const d = num(r.delta);
  const s = num(r.size);
  if (d == null || s == null) return <span style={{ color: C.textDim }}>—</span>;
  const eq = Math.round(d * s * 100);
  return (
    <span style={{ color: eq >= 0 ? C.text : C.gold, fontVariantNumeric: "tabular-nums" }}>
      {eq.toLocaleString("en-US")}
    </span>
  );
}

// ── Tags cell — colored pills like UW's tag column ───────────────────────────
const TAG_STYLE: Record<string, { label: string; color: string }> = {
  bullish: { label: "BULLISH", color: C.green },
  bearish: { label: "BEARISH", color: C.red },
  neutral: { label: "NEUTRAL", color: C.gold },
  ask_side: { label: "ASK", color: C.green },
  bid_side: { label: "BID", color: C.red },
  mid_side: { label: "MID", color: C.gold },
  no_side: { label: "NO SIDE", color: C.textDim },
  index: { label: "INDEX", color: "#a78bfa" },
  etf: { label: "ETF", color: "#a78bfa" },
  earnings_this_week: { label: "ER WK", color: C.gold },
  earnings_next_week: { label: "ER NXT", color: C.gold },
  china: { label: "CHINA", color: C.red },
  volatility: { label: "VOL", color: C.gold },
  dividend: { label: "DIV", color: C.text },
};

function TagsCell({ r }: { r: Row }) {
  const tags = Array.isArray(r.tags) ? (r.tags as unknown[]).map(String) : [];
  if (!tags.length) return <span style={{ color: C.textDim }}>—</span>;
  const known = tags.filter((t) => TAG_STYLE[t]);
  const shown = known.slice(0, 3);
  const extra = known.length - shown.length;
  return (
    <span style={{ display: "inline-flex", gap: 4, alignItems: "center" }} title={tags.join(", ")}>
      {shown.map((t) => {
        const s = TAG_STYLE[t];
        return (
          <span
            key={t}
            style={{
              fontSize: 9,
              fontWeight: 700,
              letterSpacing: 0.6,
              color: s.color,
              border: `1px solid ${s.color}`,
              padding: "1px 5px",
              borderRadius: 3,
            }}
          >
            {s.label}
          </span>
        );
      })}
      {extra > 0 && <span style={{ color: C.textDim, fontSize: 10 }}>+{extra}</span>}
    </span>
  );
}

// ── Columns (all available; user picks visible set) ──────────────────────────
interface Col {
  key: string;
  label: string;
  width?: number;
  render: (r: Row, ctx: { maxPrem: number }) => React.ReactNode;
  align?: "left" | "right" | "center";
  defaultOn: boolean;
  sortField?: string; // screener field this column sorts by
  help: string; // hover tooltip on the header explaining the column
}

const dim = (s: string) => <span style={{ color: C.textDim, fontVariantNumeric: "tabular-nums" }}>{s}</span>;
const plain = (s: string) => <span style={{ color: C.text, fontVariantNumeric: "tabular-nums" }}>{s}</span>;

// Index/vol products carry no company data — say so instead of a blank dash.
function SectorCell({ r }: { r: Row }) {
  const sector = r.sector ? String(r.sector) : "";
  if (sector) return dim(sector);
  const tags = Array.isArray(r.tags) ? (r.tags as unknown[]).map(String) : [];
  if (tags.includes("index")) return dim("Index");
  if (tags.includes("etf")) return dim("ETF");
  return (
    <span title="Index and ETF products have no sector, market cap, or earnings date." style={{ color: C.textDim }}>
      —
    </span>
  );
}

// Column order mirrors Unusual Whales' flow table exactly:
// Time · Ticker · Side · Contract · DTE · Stock · %OTM · Moneyness · Bid-Ask ·
// Spot · Fill vs Spread · Spread · Size · Premium · Volume · OI · Vol/OI ·
// Chain Bid/Ask · Share Eqv · IV · Delta · Theta · Gamma · Vega · Rho · Theo ·
// Sector · Industry · Earnings · Legs · Code · Exchange · Flags · Tags
const ALL_COLUMNS: Col[] = [
  { key: "timestamp", label: "Time (ET)", defaultOn: true, sortField: "timestamp",
    help: "When the trade printed, in Eastern (market) time. Date + clock time.",
    render: (r) => <TimeCell v={r.timestamp} /> },
  { key: "ticker", label: "Ticker", defaultOn: true,
    help: "The underlying stock, ETF, or index the option is on.",
    render: (r) => (
    <span style={{ fontWeight: 600, color: C.text, letterSpacing: 0.5 }}>{String(r.ticker || "—")}</span>
  ) },
  { key: "side", label: "Side", align: "center", defaultOn: true,
    help: "Which side of the market got hit. ASK = buyer paid up (aggressive buying). BID = seller hit the bid (aggressive selling). MID = filled in between.",
    render: (r) => <SideBadge r={r} /> },
  { key: "contract", label: "Contract", defaultOn: true, sortField: "strike",
    help: "The option: strike price, call or put, and expiration date.",
    render: (r) => <ContractCell r={r} /> },
  { key: "expiry", label: "Expiry", align: "right", defaultOn: true, sortField: "expiry",
    help: "The date this option expires. After this day the contract is gone.",
    render: (r) => plain(fmtDate(r.expiry)) },
  { key: "dte", label: "DTE", align: "right", defaultOn: true, sortField: "dte",
    help: "Days to expiry. 0 = expires today (0DTE). Small numbers = short-term bets.",
    render: (r) => plain(fmtNum(r.dte)) },
  { key: "stock", label: "Stock", align: "right", defaultOn: true, sortField: "underlying_price",
    help: "The underlying stock's price at the moment this option traded.",
    render: (r) => plain(fmtPrice(r.underlying_price)) },
  { key: "diff", label: "% OTM", align: "right", defaultOn: true, sortField: "diff",
    help: "How far the strike is from the stock price. Positive = out of the money, negative = in the money. Bigger % OTM = cheaper, riskier bet.",
    render: (r) => {
    const d = num(r.diff);
    if (d == null) return dim("—");
    return <span style={{ color: d > 0 ? C.text : C.gold, fontVariantNumeric: "tabular-nums" }}>{(d * 100).toFixed(1)}%</span>;
  } },
  { key: "moneyness", label: "Moneyness", align: "center", defaultOn: true,
    help: "Quick ITM/OTM indicator. Gold filled dot = in the money. Hollow circle = out of the money. White dot = right at the money.",
    render: (r) => <MoneynessDot r={r} /> },
  { key: "bidask", label: "Bid – Ask", align: "right", defaultOn: true,
    help: "The quoted market for this option when the trade hit: best bid and best ask.",
    render: (r) => <BidAskText r={r} /> },
  { key: "spot", label: "Fill", align: "right", defaultOn: true, sortField: "price",
    help: "The actual price per contract this trade filled at.",
    render: (r) => plain(fmtPrice(r.price)) },
  { key: "spreadbar", label: "Fill vs Spread", defaultOn: true,
    help: "Where the fill landed between bid and ask. A marker near the ask means the buyer paid up; near the bid means the seller dumped.",
    render: (r) => <FillBar r={r} /> },
  { key: "spreadval", label: "Spread", align: "right", defaultOn: false, sortField: "spread",
    help: "Bid-ask spread in dollars. Tight spread = liquid contract, easy to trade.",
    render: (r) => {
    const s = num(r.spread);
    return s == null ? plain("—") : plain(`$${s.toFixed(2)}`);
  } },
  { key: "size", label: "Size", align: "right", defaultOn: true, sortField: "size",
    help: "Number of contracts in this single print. Each contract = 100 shares of exposure.",
    render: (r) => (
    <span style={{ color: C.text, fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>{fmtNum(r.size)}</span>
  ) },
  { key: "premium", label: "Premium", align: "right", defaultOn: true, sortField: "premium",
    help: "Total dollars spent on this trade (price × size × 100). The bar shows relative size vs other rows on screen.",
    render: (r, ctx) => <PremiumCell r={r} maxPrem={ctx.maxPrem} /> },
  { key: "volume", label: "Volume", align: "right", defaultOn: true, sortField: "volume",
    help: "Total contracts traded today on this exact option.",
    render: (r) => plain(fmtNum(r.volume)) },
  { key: "oi", label: "OI", align: "right", defaultOn: true, sortField: "open_interest",
    help: "Open interest: contracts already held open from previous days. Volume above OI means fresh new positioning today.",
    render: (r) => plain(fmtNum(r.open_interest)) },
  { key: "voloi", label: "Vol/OI", defaultOn: true, sortField: "vol_oi_ratio",
    help: "Today's volume vs open interest, side by side. Green bar past 1× = unusual — more traded today than existed before.",
    render: (r) => <VolOiCell r={r} /> },
  { key: "chain", label: "Chain Bid/Ask", defaultOn: true,
    help: "Of all volume on this contract today, how much hit the ask (green, buying) vs the bid (red, selling).",
    render: (r) => <ChainBidAskBar r={r} /> },
  { key: "shareeqv", label: "Share Eqv", align: "right", defaultOn: false,
    help: "Share-equivalent exposure: delta × size × 100. Roughly how many shares of the stock this option position behaves like.",
    render: (r) => <ShareEqvCell r={r} /> },
  { key: "iv", label: "IV %", align: "right", defaultOn: true, sortField: "iv",
    help: "Implied volatility — the market's priced-in expectation of movement. Higher IV = more expensive options.",
    render: (r) => plain(fmtPct(r.iv)) },
  { key: "delta", label: "Delta", align: "right", defaultOn: false, sortField: "delta",
    help: "How much the option price moves per $1 move in the stock. Also ≈ probability of expiring in the money.",
    render: (r) => plain(fmtGreek(r.delta)) },
  { key: "theta", label: "Theta", align: "right", defaultOn: false, sortField: "theta",
    help: "Daily time decay: how many dollars the option loses each day just from time passing.",
    render: (r) => plain(fmtGreek(r.theta)) },
  { key: "gamma", label: "Gamma", align: "right", defaultOn: false, sortField: "gamma",
    help: "How fast delta changes as the stock moves. High gamma = option exposure accelerates quickly.",
    render: (r) => plain(fmtGreek(r.gamma)) },
  { key: "vega", label: "Vega", align: "right", defaultOn: false, sortField: "vega",
    help: "Sensitivity to volatility: how much the option gains if implied volatility rises 1 point.",
    render: (r) => plain(fmtGreek(r.vega)) },
  { key: "rho", label: "Rho", align: "right", defaultOn: false, sortField: "rho",
    help: "Sensitivity to interest rates. Mostly matters for long-dated options.",
    render: (r) => plain(fmtGreek(r.rho)) },
  { key: "theo", label: "Theo", align: "right", defaultOn: false, sortField: "theo",
    help: "Theoretical fair value of the option from a pricing model. Compare to the actual fill.",
    render: (r) => plain(fmtPrice(r.theo)) },
  { key: "sector", label: "Sector", defaultOn: false,
    help: "The company's market sector. Index and ETF products show Index/ETF instead.",
    render: (r) => <SectorCell r={r} /> },
  { key: "industry", label: "Industry", defaultOn: false,
    help: "The company's specific industry (finer than sector).",
    render: (r) => plain(String(r.industry || "—")) },
  { key: "earnings_dte", label: "Earnings", align: "right", defaultOn: false, sortField: "earnings_dte",
    help: "Days until the company's next earnings report. Blank for indexes/ETFs.",
    render: (r) => {
    const e = num(r.earnings_dte);
    return e == null ? plain("—") : plain(`${e}d`);
  } },
  { key: "legs", label: "Legs", align: "center", defaultOn: false,
    help: "MULTI = part of a spread (multiple options traded together as one strategy). Single = standalone trade.",
    render: (r) => {
    if (r.is_multi_leg === true) return <span style={{ color: C.gold, fontSize: 10, fontWeight: 700 }}>MULTI</span>;
    if (r.is_multi_leg === false) return plain("Single");
    return plain("—");
  } },
  { key: "code", label: "Code", align: "center", defaultOn: false,
    help: "The exchange's raw condition code for how this trade executed (e.g. AUTO = standard electronic fill, SLAN = auction).",
    render: (r) => plain(String(r.trade_codes || "—")) },
  { key: "exchange", label: "Exchange", align: "center", defaultOn: false,
    help: "Which options exchange the trade printed on.",
    render: (r) => plain(String(r.exchange || "—")) },
  { key: "flags", label: "Flags", align: "left", defaultOn: true,
    help: "Execution badges: SWEEP (aggressive multi-exchange order), CROSS, FLOOR, UNUSUAL (triggered a flow alert), CANCELED.",
    render: (r) => <FlagsCell r={r} /> },
  { key: "tagscol", label: "Tags", align: "left", defaultOn: true,
    help: "Quick-read labels on the print: sentiment (BULLISH/BEARISH), fill side, product type (ETF/INDEX), earnings timing.",
    render: (r) => <TagsCell r={r} /> },
  // Extra columns (not in UW's table but useful; off by default)
  { key: "type", label: "C/P", align: "center", defaultOn: false,
    help: "Call or put, as a standalone column.",
    render: (r) => {
    const t = String(r.type || "").toLowerCase();
    const call = t === "call";
    return (
      <span style={{ fontSize: 10, fontWeight: 600, letterSpacing: 1, color: call ? C.green : t === "put" ? C.red : C.textDim }}>
        {call ? "CALL" : t === "put" ? "PUT" : "—"}
      </span>
    );
  } },
  { key: "strike", label: "Strike", align: "right", defaultOn: false, sortField: "strike",
    help: "The option's strike price, as a standalone column.",
    render: (r) => plain(fmtPrice(r.strike)) },
  { key: "marketcap", label: "Mkt Cap", align: "right", defaultOn: false, sortField: "marketcap",
    help: "Company market cap (or fund size for ETFs), in millions/billions/trillions.",
    render: (r) => plain(fmtMarketCap(r.marketcap)) },
  { key: "sentiment", label: "Sentiment", align: "center", defaultOn: false,
    help: "Bullish or bearish read of the print, from option type + fill side.",
    render: (r) => {
    const s = String(r.sentiment || "").toLowerCase();
    if (!s) return plain("—");
    const color = s === "bullish" ? C.green : s === "bearish" ? C.red : C.gold;
    return <span style={{ color, fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.6 }}>{s}</span>;
  } },
  { key: "contract_id", label: "Contract ID", defaultOn: false,
    help: "The full OCC option symbol (ticker + expiry + type + strike).",
    render: (r) => (
    <span style={{ color: C.text, fontSize: 11, fontVariantNumeric: "tabular-nums" }}>
      {String(r.option_contract_ticker || "—")}
    </span>
  ) },
];

// v4: expiry column on by default + header tooltips — bump so stale saved
// column prefs don't hide the new layout.
const COLS_STORAGE_KEY = "xynth_flow_cols_v4";

// Toolbar sort dropdown options (mirrors UW's "Sort" control). All of these
// sort the loaded page locally — verified the UW API only honors time order,
// so page-local sorting is exactly what UW's own site does too.
const SORT_OPTIONS: { v: string; label: string }[] = [
  { v: "timestamp", label: "Time" },
  { v: "premium", label: "Premium" },
  { v: "size", label: "Size" },
  { v: "volume", label: "Volume" },
  { v: "open_interest", label: "Open Interest" },
  { v: "vol_oi_ratio", label: "Vol/OI" },
  { v: "strike", label: "Strike" },
  { v: "expiry", label: "Expiry" },
  { v: "dte", label: "DTE" },
  { v: "price", label: "Fill Price" },
  { v: "underlying_price", label: "Stock Price" },
  { v: "marketcap", label: "Market Cap" },
  { v: "earnings_dte", label: "Earnings" },
  { v: "iv", label: "IV" },
  { v: "delta", label: "Delta" },
  { v: "gamma", label: "Gamma" },
  { v: "theta", label: "Theta" },
  { v: "vega", label: "Vega" },
  { v: "rho", label: "Rho" },
  { v: "theo", label: "Theo" },
  { v: "spread", label: "Spread" },
  { v: "diff", label: "% OTM" },
];

const RESULT_COUNTS = [50, 100, 200, 300, 500];

// Insert a live row into an already-sorted list (non-timestamp sorts).
function insertSorted(rows: Row[], row: Row, orderBy: string, asc: boolean, cap: number): Row[] {
  const v = num(row[orderBy]);
  if (v == null) return rows; // unsortable value: skip rather than corrupt order
  let lo = 0;
  let hi = rows.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    const mv = num(rows[mid][orderBy]);
    const before = mv == null ? true : asc ? v < mv : v > mv;
    if (before) hi = mid;
    else lo = mid + 1;
  }
  const next = [...rows.slice(0, lo), row, ...rows.slice(lo)];
  return next.slice(0, cap);
}

function shallowEqualParams(a: FlowParams, b: FlowParams): boolean {
  const ka = Object.keys(a).filter((k) => a[k] !== "");
  const kb = Object.keys(b).filter((k) => b[k] !== "");
  if (ka.length !== kb.length) return false;
  return ka.every((k) => a[k] === b[k]);
}

// ── Memoized table ────────────────────────────────────────────────────────────
// The tape is the heaviest thing on the page (up to 500 rows × 20+ columns).
// It's split out and memoized so keystrokes in filter inputs / the ticker box
// (which only change DRAFT state) never re-render a single cell. Rows are
// individually memoized too, so a live print only renders the new row.

const FlowRow = memo(function FlowRow({
  r,
  columns,
  maxPrem,
  pulseCols,
}: {
  r: Row;
  columns: Col[];
  maxPrem: number;
  pulseCols: Set<string>;
}) {
  const bull = rowBull(r);
  const edge = bull > 0 ? C.green : bull < 0 ? C.red : "transparent";
  return (
    <tr style={{ borderBottom: `1px solid ${C.lineSoft}` }}>
      {columns.map((c, ci) => (
        <td
          key={c.key}
          className={pulseCols.has(c.key) ? "flow-td flow-cell-glow" : "flow-td"}
          style={{
            textAlign: c.align || "left",
            whiteSpace: "nowrap",
            borderLeft: ci === 0 ? `2px solid ${edge}` : undefined,
          }}
        >
          {c.render(r, { maxPrem })}
        </td>
      ))}
    </tr>
  );
});

interface FlowTableProps {
  columns: Col[];
  rows: Row[];
  loading: boolean;
  error: string | null;
  pulseCols: Set<string>;
  orderBy: string;
  orderDir: "asc" | "desc";
  onSort: (field: string) => void;
  maxPrem: number;
}

const FlowTable = memo(function FlowTable({
  columns,
  rows,
  loading,
  error,
  pulseCols,
  orderBy,
  orderDir,
  onSort,
  maxPrem,
}: FlowTableProps) {
  return (
    <div style={{ flex: 1, overflow: "auto", WebkitOverflowScrolling: "touch" }}>
      <table className="flow-table" style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            {columns.map((c) => {
              const sortable = !!c.sortField;
              const isSorted = sortable && c.sortField === orderBy;
              return (
                <th
                  key={c.key}
                  data-colkey={c.key}
                  onClick={sortable ? () => onSort(c.sortField!) : undefined}
                  className={[
                    "flow-th",
                    pulseCols.has(c.key) ? "flow-col-pulse" : "",
                    sortable ? "flow-th-sortable" : "",
                  ].join(" ").trim()}
                  style={{
                    position: "sticky",
                    top: 0,
                    zIndex: 1,
                    textAlign: c.align || "left",
                    fontWeight: 700,
                    letterSpacing: 1.4,
                    textTransform: "uppercase",
                    color: isSorted ? C.accent : C.text,
                    background: C.bg,
                    borderBottom: `2px solid ${isSorted ? C.accent : C.line}`,
                    whiteSpace: "nowrap",
                    cursor: sortable ? "pointer" : "default",
                    userSelect: "none",
                  }}
                >
                  <HoverTip C={C} text={c.help}>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, justifyContent: c.align === "right" ? "flex-end" : c.align === "center" ? "center" : "flex-start" }}>
                      {c.label}
                      {sortable && (
                        isSorted ? (
                          // Active sort: solid emerald arrow.
                          <svg width="9" height="9" viewBox="0 0 10 10" style={{ flexShrink: 0 }}>
                            <path
                              d={orderDir === "asc" ? "M5 1 L9 8 L1 8 Z" : "M5 9 L1 2 L9 2 Z"}
                              fill={C.accent}
                            />
                          </svg>
                        ) : (
                          // Sortable but inactive: stacked arrows, always visible,
                          // brighter on hover (CSS class below).
                          <svg className="flow-sort-hint" width="8" height="11" viewBox="0 0 8 12" style={{ flexShrink: 0 }}>
                            <path d="M4 0 L7.5 4.5 L0.5 4.5 Z" fill="rgba(255,255,255,0.45)" />
                            <path d="M4 12 L0.5 7.5 L7.5 7.5 Z" fill="rgba(255,255,255,0.45)" />
                          </svg>
                        )
                      )}
                    </span>
                  </HoverTip>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {loading &&
            Array.from({ length: 14 }).map((_, i) => (
              <tr key={`skel-${i}`} style={{ borderBottom: `1px solid ${C.lineSoft}` }}>
                {columns.map((c) => (
                  <td key={c.key} className="flow-td">
                    <div
                      className="flow-skel"
                      style={{
                        height: 10,
                        borderRadius: 3,
                        background: "rgba(255,255,255,0.14)",
                        width: `${45 + ((i * 7 + c.key.length * 13) % 40)}%`,
                        marginLeft: c.align === "right" ? "auto" : c.align === "center" ? "auto" : 0,
                        marginRight: c.align === "center" ? "auto" : 0,
                        animationDelay: `${(i % 7) * 90}ms`,
                      }}
                    />
                  </td>
                ))}
              </tr>
            ))}
          {!loading &&
            rows.map((r, i) => (
              <FlowRow
                key={rowKey(r, i)}
                r={r}
                columns={columns}
                maxPrem={maxPrem}
                pulseCols={pulseCols}
              />
            ))}
        </tbody>
      </table>

      {!loading && rows.length === 0 && (
        <div style={{ padding: 48, textAlign: "center", fontSize: 13 }}>
          <span style={{ color: error ? C.red : "rgba(255,255,255,0.85)" }}>
            {error || "No trades match these filters yet. New prints stream in while the market is open."}
          </span>
        </div>
      )}
    </div>
  );
});

// ── Main component ────────────────────────────────────────────────────────────
export default function FlowClient({ initialParams, initial }: Props) {
  const router = useRouter();
  // Live data requires a signed-in account (any tier — free included).
  // Anonymous visitors see the SSR teaser rows blurred + an inline unlock CTA.
  const { user, loading: authLoading } = useAuth();
  const locked = !authLoading && !user;
  const initialActiveCount = countActiveFilters(initialParams);
  // applied = drives fetch/stream/URL. draft = what the user is editing.
  const [applied, setApplied] = useState<FlowParams>(initialParams);
  const [draft, setDraft] = useState<FlowParams>(initialParams);
  const [rows, setRows] = useState<Row[]>(initial?.data || []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [live, setLive] = useState(true);
  const [buffer, setBuffer] = useState<Row[]>([]);
  const [streamState, setStreamState] = useState<"connecting" | "live" | "off">("connecting");
  const [sidebarOpen, setSidebarOpen] = useState(initialActiveCount > 0);
  const [filterSummaryOpen, setFilterSummaryOpen] = useState(initialActiveCount > 0);
  const [focusFilterKey, setFocusFilterKey] = useState<string | null>(null);
  // Exactly one toolbar dropdown open at a time — opening one closes the rest.
  const [openMenu, setOpenMenu] = useState<"cols" | "sort" | "rows" | null>(null);
  // Paging: stack of "older_than" cursors. Empty = live page (newest).
  const [pageCursors, setPageCursors] = useState<string[]>([]);
  // How many rows the last REST fetch returned. A short page (< limit) means
  // the tape has no more history for these filters → Next gets disabled.
  const [lastFetchCount, setLastFetchCount] = useState<number>(initial?.data?.length ?? 0);
  const [pulseCols, setPulseCols] = useState<Set<string>>(new Set());
  const [visibleCols, setVisibleCols] = useState<Set<string>>(
    () => new Set(ALL_COLUMNS.filter((c) => c.defaultOn).map((c) => c.key))
  );
  // Phone layout (<768px): the sidebar becomes a full-screen drawer and the
  // toolbar compacts. Tablet scaling is pure CSS (media queries below).
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  const dirty = useMemo(() => !shallowEqualParams(draft, applied), [draft, applied]);

  // Mirror of visibleCols readable synchronously inside stable callbacks.
  const visibleColsRef = useRef(visibleCols);
  visibleColsRef.current = visibleCols;

  // Restore column prefs after mount (avoids SSR/localStorage mismatch).
  useEffect(() => {
    try {
      const saved = localStorage.getItem(COLS_STORAGE_KEY);
      if (saved) {
        const keys = JSON.parse(saved) as string[];
        if (Array.isArray(keys) && keys.length) setVisibleCols(new Set(keys));
      }
    } catch {
      /* ignore */
    }
  }, []);

  // Bulk column selection: all columns, the default set, or a minimal set
  // ("none" keeps time + ticker so the table is never headless).
  const setAllCols = useCallback((mode: "all" | "defaults" | "none") => {
    const keys =
      mode === "all"
        ? ALL_COLUMNS.map((c) => c.key)
        : mode === "defaults"
          ? ALL_COLUMNS.filter((c) => c.defaultOn).map((c) => c.key)
          : ["timestamp", "ticker"];
    const next = new Set(keys);
    setVisibleCols(next);
    try {
      localStorage.setItem(COLS_STORAGE_KEY, JSON.stringify(Array.from(next)));
    } catch {
      /* ignore */
    }
  }, []);

  const toggleCol = useCallback((key: string) => {
    // Decide add-vs-remove NOW (the setState updater runs later, after this
    // handler returns, so a flag set inside it would still be stale here).
    const added = !visibleColsRef.current.has(key);
    setVisibleCols((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        if (next.size > 1) next.delete(key);
      } else {
        next.add(key);
      }
      try {
        localStorage.setItem(COLS_STORAGE_KEY, JSON.stringify(Array.from(next)));
      } catch {
        /* ignore */
      }
      return next;
    });
    // Adding a column: scroll it into view (if off-screen) and shake/glow it
    // so the user sees exactly where it landed. Removing stays silent.
    if (added) {
      setPulseCols(new Set([key]));
      if (pulseTimer.current) clearTimeout(pulseTimer.current);
      pulseTimer.current = setTimeout(() => setPulseCols(new Set()), 2100);
      requestAnimationFrame(() => {
        setTimeout(() => {
          const th = document.querySelector(`th[data-colkey="${key}"]`);
          th?.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
        }, 60);
      });
    }
  }, []);

  const columns = useMemo(
    () => ALL_COLUMNS.filter((c) => visibleCols.has(c.key)),
    [visibleCols]
  );

  const liveRef = useRef(live);
  liveRef.current = live;
  const rowsRef = useRef<Row[]>([]);
  const appliedRef = useRef(applied);
  appliedRef.current = applied;
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const esRef = useRef<EventSource | null>(null);
  const pulseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Columns to pulse once the NEXT fetch delivers rows (see applyDraft).
  const pendingPulseRef = useRef<Set<string> | null>(null);
  const MAX_ROWS = 300;

  const activeCount = useMemo(() => countActiveFilters(applied), [applied]);
  const citedSummary = useMemo(() => citedFilterSummary(applied), [applied]);
  const previousActiveCount = useRef(activeCount);

  // Client-only filters + optional print aggregation, over REST + live rows.
  const displayRows = useMemo(() => {
    let out = applyClientFilters(rows, applied);
    if (applied.aggregated === "true") out = aggregateRows(out);
    return out;
  }, [rows, applied]);

  rowsRef.current = rows;
  const displayRowsRef = useRef<Row[]>(displayRows);
  displayRowsRef.current = displayRows;

  const maxPrem = useMemo(() => {
    let m = 0;
    for (const r of displayRows) m = Math.max(m, num(r.premium) || 0);
    return m;
  }, [displayRows]);

  // ── URL sync ──
  const syncUrl = useCallback((p: FlowParams) => {
    const sp = new URLSearchParams();
    Object.entries(p).forEach(([k, v]) => {
      if (v !== "") sp.set(k, v);
    });
    const qs = sp.toString();
    router.replace(qs ? `/flow?${qs}` : "/flow", { scroll: false });
  }, [router]);

  // ── REST fetch (on apply / paging) ──
  // olderThan: page-back cursor. excludeIds: trade ids already shown on the
  // previous page (the cursor is <=, so boundary prints would duplicate).
  const fetchRows = useCallback(
    async (p: FlowParams, olderThan?: string, excludeIds?: Set<string>) => {
      setLoading(true);
      setError(null);
      const t0 = Date.now();
      const body = paramsToScreenerBody(p);
      if (olderThan) {
        // The screener only supports less_or_equal on timestamps (maps to the
        // API's older_than param). Strict "less" is NOT pushed down and would
        // silently return the same newest page again.
        body.filters.push({ field: "timestamp", op: "less_or_equal", value: olderThan });
      }
      const doFetch = async () => {
        const token = await authService.getAuthToken();
        return fetch(`${API_BASE_URL}/api/data/screeners/options_flow_trades`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify(body),
        });
      };
      try {
        let res: Response;
        try {
          res = await doFetch();
        } catch {
          // Network blip (deploy, wifi hiccup): retry once before surfacing.
          logClient("flow_fetch_retry", { ms: Date.now() - t0 });
          await new Promise((r) => setTimeout(r, 1200));
          res = await doFetch();
        }
        if (!res.ok) {
          const j = await res.json().catch(() => null);
          logClient("flow_fetch_http_error", { status: res.status, ms: Date.now() - t0, detail: j?.detail });
          throw new Error(j?.detail || `Server error (${res.status}) — try again in a moment`);
        }
        const j = await res.json();
        let data: Row[] = j?.data || [];
        // Count what the SERVER returned (pre-dedupe): a short page means the
        // history is exhausted, regardless of boundary-id trimming.
        setLastFetchCount(data.length);
        if (excludeIds && excludeIds.size) {
          data = data.filter((r) => !r.id || !excludeIds.has(String(r.id)));
        }
        setRows(data);
        return data;
      } catch (e) {
        const msg = e instanceof Error ? e.message : "Could not load flow";
        logClient("flow_fetch_error", { message: msg, ms: Date.now() - t0 });
        setError(
          msg === "Failed to fetch"
            ? "Connection dropped — check your network and hit Apply to retry"
            : msg
        );
        setRows([]);
        setLastFetchCount(0);
        return [];
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // ── Apply / draft / reset ──
  // Sequence on Apply: (1) auto-show any hidden columns the changed filters
  // touch, (2) show the loading skeleton while fetching, (3) once rows are
  // actually back, scroll the first affected column into view and pulse.
  const applyDraft = useCallback(() => {
    const d = draftRef.current;
    const prev = appliedRef.current;
    if (shallowEqualParams(d, prev)) return;
    const cols = changedColumns(prev, d);
    if (cols.size) {
      setVisibleCols((prevVis) => {
        const missing = Array.from(cols).filter(
          (k) => !prevVis.has(k) && ALL_COLUMNS.some((c) => c.key === k)
        );
        if (!missing.length) return prevVis;
        const next = new Set(prevVis);
        missing.forEach((k) => next.add(k));
        try {
          localStorage.setItem(COLS_STORAGE_KEY, JSON.stringify(Array.from(next)));
        } catch {
          /* ignore */
        }
        return next;
      });
      pendingPulseRef.current = cols;
    } else {
      pendingPulseRef.current = null;
    }
    setPulseCols(new Set());
    setApplied(d);
    syncUrl(d);
    setBuffer([]);
    fetchRows(d).then((data) => {
      const pending = pendingPulseRef.current;
      pendingPulseRef.current = null;
      // Only celebrate when there's actually data on screen to look at.
      if (!pending || !pending.size || !data.length) return;
      setPulseCols(pending);
      if (pulseTimer.current) clearTimeout(pulseTimer.current);
      pulseTimer.current = setTimeout(() => setPulseCols(new Set()), 2100);
      const first = Array.from(pending)[0];
      requestAnimationFrame(() => {
        setTimeout(() => {
          const th = document.querySelector(`th[data-colkey="${first}"]`);
          th?.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
        }, 60);
      });
    });
  }, [syncUrl, fetchRows]);

  // ── Ticker quick-search auto-applies: typing (debounced) or Enter fires the
  //    fetch on its own — no Apply button in the toolbar. Only the ticker param
  //    is committed; other sidebar draft edits stay pending until sidebar Apply.
  const applyTickerNow = useCallback(() => {
    const t = (draftRef.current.ticker || "").trim();
    const prev = appliedRef.current;
    if (t === (prev.ticker || "")) return;
    const next: FlowParams = { ...prev };
    if (t) next.ticker = t;
    else delete next.ticker;
    setApplied(next);
    syncUrl(next);
    setBuffer([]);
    pendingPulseRef.current = new Set(["ticker"]);
    fetchRows(next).then((data) => {
      const pending = pendingPulseRef.current;
      pendingPulseRef.current = null;
      if (!pending || !data.length) return;
      setPulseCols(pending);
      if (pulseTimer.current) clearTimeout(pulseTimer.current);
      pulseTimer.current = setTimeout(() => setPulseCols(new Set()), 2100);
    });
  }, [syncUrl, fetchRows]);

  const tickerDraft = draft.ticker || "";
  const tickerApplied = applied.ticker || "";
  useEffect(() => {
    if (tickerDraft === tickerApplied) return;
    const h = setTimeout(applyTickerNow, 700);
    return () => clearTimeout(h);
  }, [tickerDraft, tickerApplied, applyTickerNow]);

  // Any click outside an open toolbar dropdown closes it.
  useEffect(() => {
    if (!openMenu) return;
    const h = (e: MouseEvent) => {
      const t = e.target as HTMLElement | null;
      if (!t || !t.closest("[data-flow-menu]")) setOpenMenu(null);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [openMenu]);

  const setDraftParam = useCallback((key: string, value: string | null) => {
    setDraft((prev) => {
      const next = { ...prev };
      if (value == null || value === "") delete next[key];
      else next[key] = value;
      return next;
    });
  }, []);

  // ── Prev/Next paging over history via timestamp cursor ──
  // Next only makes sense when the last fetch filled its page: if we asked for
  // 500 and got 2, there is no older history for these filters.
  const pageLimit = Math.min(Number(applied.limit) || FLOW_DEFAULTS.limit, 500);
  const hasMorePages = lastFetchCount >= pageLimit;
  const pageNext = useCallback(() => {
    // Oldest visible row's timestamp becomes the "older_than" cursor. The
    // cursor filter is <= (only op the API supports), so boundary prints with
    // exactly that timestamp are excluded by id to avoid duplicates.
    const cur = rowsRef.current;
    if (!cur.length) return;
    let oldest: string | null = null;
    for (const r of cur) {
      const t = r.timestamp ? String(r.timestamp) : null;
      if (t && (oldest == null || t < oldest)) oldest = t;
    }
    if (!oldest) return;
    const boundary = oldest;
    const seen = new Set<string>(
      cur.filter((r) => String(r.timestamp) === boundary && r.id).map((r) => String(r.id))
    );
    setLive(false); // paging back = not tailing the live edge
    setPageCursors((prev) => [...prev, boundary]);
    fetchRows(appliedRef.current, boundary, seen);
  }, [fetchRows]);

  const pagePrev = useCallback(() => {
    setPageCursors((prev) => {
      const next = prev.slice(0, -1);
      const cursor = next[next.length - 1];
      fetchRows(appliedRef.current, cursor);
      if (next.length === 0) setLive(true); // back to the newest page → resume tape
      return next;
    });
  }, [fetchRows]);

  // ── CSV export of exactly what's displayed ──
  const exportCsv = useCallback(() => {
    const rows = displayRowsRef.current;
    if (!rows.length) return;
    const fields = [
      "timestamp", "ticker", "side", "type", "strike", "expiry", "dte",
      "underlying_price", "diff", "bid", "ask", "price", "spread", "size",
      "premium", "volume", "open_interest", "vol_oi_ratio", "ask_perc",
      "bid_perc", "iv", "delta", "theta", "gamma", "vega", "rho", "theo",
      "sector", "industry", "earnings_dte", "marketcap", "trade_codes",
      "exchange", "is_sweep", "is_cross", "is_floor", "unusual", "tags",
      "option_contract_ticker",
    ];
    const esc = (v: unknown): string => {
      if (v == null) return "";
      const s = Array.isArray(v) ? v.join("|") : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const csv = [
      fields.join(","),
      ...rows.map((r) => fields.map((f) => esc(r[f])).join(",")),
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `xynth-flow-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-")}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  }, []);

  const resetFilters = useCallback(() => {
    const kept: FlowParams = {};
    if (applied.limit) kept.limit = applied.limit;
    setDraft(kept);
    setApplied(kept);
    syncUrl(kept);
    setBuffer([]);
    setPageCursors([]);
    setLive(true);
    fetchRows(kept);
    setPulseCols(new Set());
    setFilterSummaryOpen(false);
  }, [applied.limit, syncUrl, fetchRows]);

  useEffect(() => {
    if (activeCount > 0 && activeCount !== previousActiveCount.current) {
      setFilterSummaryOpen(true);
    }
    previousActiveCount.current = activeCount;
  }, [activeCount]);

  // ── Sorting: header click applies instantly (it's a view change) ──
  const orderBy = applied.order_by || FLOW_DEFAULTS.order_by;
  const orderDir = applied.order_dir === "asc" ? "asc" : "desc";
  const setSort = useCallback((field: string) => {
    const prev = appliedRef.current;
    const cur = prev.order_by || FLOW_DEFAULTS.order_by;
    const curDir = prev.order_dir === "asc" ? "asc" : "desc";
    const next: FlowParams = { ...prev };
    if (cur === field) {
      next.order_dir = curDir === "desc" ? "asc" : "desc";
      next.order_by = field;
    } else {
      next.order_by = field;
      next.order_dir = "desc";
    }
    if (next.order_by === FLOW_DEFAULTS.order_by) delete next.order_by;
    if (next.order_dir === "desc") delete next.order_dir;
    setApplied(next);
    // Keep the draft's sort in lockstep so sorting never marks filters dirty.
    setDraft((d) => {
      const nd = { ...d };
      if (next.order_by) nd.order_by = next.order_by;
      else delete nd.order_by;
      if (next.order_dir) nd.order_dir = next.order_dir;
      else delete nd.order_dir;
      return nd;
    });
    syncUrl(next);
    setBuffer([]);
    fetchRows(next);
  }, [syncUrl, fetchRows]);

  // ── Live SSE stream (restarts only when APPLIED filters change) ──
  // The stream is account-gated: EventSource can't set headers, so the
  // Firebase ID token rides in the `auth` query param (stripped server-side
  // before the request is forwarded upstream).
  useEffect(() => {
    if (esRef.current) {
      esRef.current.close();
      esRef.current = null;
    }
    if (!user) {
      setStreamState("off");
      return;
    }
    setStreamState("connecting");
    let es: EventSource | null = null;
    let cancelled = false;
    let flushTimer: ReturnType<typeof setTimeout> | null = null;

    (async () => {
      const token = await authService.getAuthToken();
      if (cancelled || !token) return;
      const qs = paramsToStreamQuery(applied);
      const url = `${API_BASE_URL}/api/data/flow/stream?${qs ? `${qs}&` : ""}auth=${encodeURIComponent(token)}`;
      es = new EventSource(url);
      esRef.current = es;
      let gotError = false;

      es.addEventListener("status", () => setStreamState("live"));
      // Batch live prints: collect everything that arrives, flush to React at
      // most ~4×/sec. During busy tape (SPX open) this is the difference between
      // one render per print (jank, typing lag) and one render per 250ms.
      const pending: Row[] = [];
      const flush = () => {
      flushTimer = null;
      if (!pending.length) return;
      const batch = pending.splice(0, pending.length);
      const p = appliedRef.current;
      const ob = p.order_by || FLOW_DEFAULTS.order_by;
      const asc = p.order_dir === "asc";
      if (liveRef.current) {
        setRows((prev) => {
          if (ob === "timestamp") {
            // batch arrives oldest→newest; newest-first display wants reverse
            return asc
              ? [...prev, ...batch].slice(-MAX_ROWS)
              : [...batch.slice().reverse(), ...prev].slice(0, MAX_ROWS);
          }
          let next = prev;
          for (const row of batch) next = insertSorted(next, row, ob, asc, MAX_ROWS);
          return next;
        });
      } else {
        setBuffer((prev) => [...batch.slice().reverse(), ...prev].slice(0, MAX_ROWS));
      }
    };
      es.addEventListener("trade", (ev) => {
        let row: Row | null = null;
        try {
          row = JSON.parse((ev as MessageEvent).data);
        } catch {
          return;
        }
        if (!row) return;
        if (!rowPassesClientFilters(row, appliedRef.current)) return;
        pending.push(row);
        if (!flushTimer) flushTimer = setTimeout(flush, 250);
      });
      es.addEventListener("error", () => {
        setStreamState("off");
        if (!gotError) {
          gotError = true;
          logClient("flow_stream_error", { readyState: es?.readyState });
        }
      });
      es.onopen = () => {
        setStreamState("live");
        gotError = false;
      };
    })();

    return () => {
      cancelled = true;
      if (flushTimer) clearTimeout(flushTimer);
      es?.close();
      esRef.current = null;
    };
  }, [applied, user]);

  // SSR delivers only the capped teaser sample (crawler/anonymous HTML).
  // Once a signed-in account is confirmed, pull the full first page.
  useEffect(() => {
    if (user) fetchRows(appliedRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // ── Play/pause: flush buffer on resume ──
  const toggleLive = useCallback(() => {
    setLive((prev) => {
      const nextLive = !prev;
      if (nextLive && buffer.length) {
        setRows((r) => [...buffer, ...r].slice(0, MAX_ROWS));
        setBuffer([]);
      }
      return nextLive;
    });
  }, [buffer]);

  // Toolbar buttons: clearly visible borders (no hairlines up here) and a
  // faint fill so they read as buttons against pure black.
  const btn: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: 7,
    background: "rgba(255,255,255,0.03)",
    border: `1px solid ${C.lineStrong}`,
    color: C.text,
    padding: "7px 13px",
    cursor: "pointer",
    fontSize: 12,
    letterSpacing: 0.5,
    borderRadius: 4,
    whiteSpace: "nowrap",
  };
  const menuPanel: React.CSSProperties = {
    position: "absolute",
    top: "calc(100% + 6px)",
    left: 0,
    zIndex: 30,
    background: C.panel,
    border: `1px solid ${C.lineStrong}`,
    borderRadius: 6,
    padding: 6,
    boxShadow: "0 12px 40px rgba(0,0,0,0.85)",
  };

  return (
    <div style={{ minHeight: "100vh", background: C.bg, color: C.text, display: "flex", flexDirection: "column", fontFamily: "var(--font-geist-sans, ui-sans-serif, system-ui)" }}>
      <div style={{ flex: 1, minHeight: 0, display: "flex", position: "relative" }}>
      {locked && (
        <UnlockOverlay
          title="Unlock the live options flow tape, free"
          subtitle="Every sweep, block and floor print streams in real time. Create a free Xynth account to unlock the full tape, filters, and CSV export."
          bullets={[
            "Real-time unusual options activity, every print",
            "40+ filters: premium, sweeps, Greeks, OTM %, earnings",
            "Live streaming tape + full history paging",
            "Free forever, no credit card required",
          ]}
        />
      )}
      <div
        style={
          locked
            ? { display: "flex", flex: 1, minWidth: 0, filter: "blur(7px)", pointerEvents: "none", userSelect: "none" }
            : { display: "flex", flex: 1, minWidth: 0 }
        }
        aria-hidden={locked || undefined}
      >
      {/* Apply-pulse animation: subtle shake + green wash that fades over 2s. */}
      <style>{`
        @keyframes flowColPulse {
          0%   { transform: translateX(0);    background-color: rgba(16,185,129,0.22); }
          12%  { transform: translateX(-2px); }
          24%  { transform: translateX(2px);  }
          36%  { transform: translateX(-1px); }
          48%  { transform: translateX(1px);  background-color: rgba(16,185,129,0.14); }
          60%  { transform: translateX(0);    }
          100% { transform: translateX(0);    background-color: transparent; }
        }
        .flow-col-pulse { animation: flowColPulse 2s ease-out both; }
        @keyframes flowCellGlow {
          0%   { background-color: rgba(16,185,129,0.10); }
          100% { background-color: transparent; }
        }
        .flow-cell-glow { animation: flowCellGlow 2s ease-out both; }
        /* Sortable headers: whole header brightens + underline on hover, and the
           stacked sort arrows go full white so sortability is obvious. */
        .flow-th-sortable:hover { background-color: rgba(255,255,255,0.05) !important; }
        .flow-th-sortable:hover .flow-sort-hint path { fill: #ffffff; }
        /* Loading skeleton: slow, minimal breathing shimmer. */
        @keyframes flowSkeleton {
          0%   { opacity: 0.35; }
          50%  { opacity: 0.9; }
          100% { opacity: 0.35; }
        }
        .flow-skel { animation: flowSkeleton 1.6s ease-in-out infinite; }
        .flow-live-btn:hover { filter: brightness(1.25); }
        /* Kill native number-input spinners everywhere on this page. */
        input[type="number"]::-webkit-outer-spin-button,
        input[type="number"]::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
        input[type="number"] { -moz-appearance: textfield; appearance: textfield; }

        /* Table cells: sizing lives here (not inline) so breakpoints can scale it. */
        .flow-table { font-size: 12.5px; }
        .flow-th { padding: 10px 10px; font-size: 10px; }
        .flow-td { padding: 8px 10px; }
        .flow-table tbody tr:nth-child(even) { background: rgba(255,255,255,0.015); }

        /* ── Responsive scaling ─────────────────────────────────────────────
           Tablets / small desktops: shrink buttons + fonts FIRST so the
           toolbar stays on one line instead of wrapping into a pile. */
        @media (max-width: 1400px) {
          .flow-btn { padding: 6px 10px !important; font-size: 11px !important; gap: 5px !important; }
          .flow-toolbar { gap: 8px !important; padding: 12px 14px !important; }
          .flow-page-btn { padding: 6px 10px !important; font-size: 11px !important; }
          .flow-ticker-input { font-size: 11px !important; padding: 6px 26px 6px 10px !important; }
        }
        @media (max-width: 1150px) {
          .flow-btn { padding: 5px 8px !important; font-size: 10.5px !important; }
          .flow-toolbar { gap: 6px !important; padding: 10px 12px !important; }
          .flow-page-btn { padding: 5px 8px !important; font-size: 10.5px !important; }
          .flow-title { font-size: 15px !important; letter-spacing: 2px !important; }
          .flow-table { font-size: 12px; }
          .flow-th { padding: 8px 8px; font-size: 9.5px; }
          .flow-td { padding: 7px 8px; }
        }
        /* Phones: toolbar becomes stacked zones; everything tightens. */
        @media (max-width: 767px) {
          .flow-toolbar { gap: 6px !important; padding: 10px 10px !important; }
          .flow-zone1 { width: 100%; }
          .flow-zone1 > * { flex: 1 1 auto; }
          .flow-zone1 .flow-btn { justify-content: center; }
          .flow-zone3 { width: 100%; }
          .flow-zone2 { order: 4; width: 100%; margin-inline: 0 !important; }
          .flow-zone2 button, .flow-zone2 span { flex: 1; justify-content: center; }
          .flow-title-strip { padding: 12px 12px 0 !important; }
          .flow-title { font-size: 14px !important; letter-spacing: 1.5px !important; }
          .flow-title-sub { display: none; }
          .flow-table { font-size: 11.5px; }
          .flow-th { padding: 8px 7px; font-size: 9px; letter-spacing: 1px !important; }
          .flow-td { padding: 7px 7px; }
          .flow-footer { padding: 8px 12px !important; font-size: 10px !important; gap: 10px !important; }
        }
      `}</style>

      <FlowFilterSidebar
        open={sidebarOpen}
        mobile={isMobile}
        draft={draft}
        dirty={dirty}
        onClose={() => setSidebarOpen(false)}
        onDraft={setDraftParam}
        onApply={applyDraft}
        activeCount={activeCount}
        C={C}
        focusKey={focusFilterKey}
        onFocusSettled={() => setFocusFilterKey(null)}
      />

      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
        {/* Title strip */}
        <div
          className="flow-title-strip"
          style={{
            display: "flex",
            alignItems: "baseline",
            gap: 14,
            padding: "18px 20px 0",
          }}
        >
          <h1 className="flow-title" style={{ margin: 0, fontSize: 17, fontWeight: 600, letterSpacing: 3, textTransform: "uppercase" }}>
            Options Flow
          </h1>
          <span className="flow-title-sub" style={{ fontSize: 11, color: C.textDim, letterSpacing: 1, textTransform: "uppercase" }}>
            Real-time tape · every print
          </span>
        </div>

        {activeCount > 0 && filterSummaryOpen && (
          <div
            style={{
              margin: "12px 20px 0",
              border: `1px solid ${C.lineStrong}`,
              background: "rgba(255,255,255,0.025)",
              borderRadius: 6,
              padding: "8px 10px",
              display: "flex",
              alignItems: "center",
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            <span style={{ color: C.text, fontSize: 10, fontWeight: 800, letterSpacing: 1.4, textTransform: "uppercase" }}>
              Filtered tape
            </span>
            <span style={{ color: C.textDim, fontSize: 11, letterSpacing: 0.3 }}>
              {activeCount} active
            </span>
            {citedSummary.map((chip) => (
              <button
                key={chip.key}
                title="Click to focus this filter"
                onClick={() => {
                  setSidebarOpen(true);
                  setFocusFilterKey(chip.key);
                }}
                style={{
                  border: `1px solid rgba(255,255,255,0.20)`,
                  background: "rgba(255,255,255,0.035)",
                  color: C.text,
                  borderRadius: 999,
                  padding: "4px 9px",
                  fontSize: 10.5,
                  fontWeight: 650,
                  letterSpacing: 0.3,
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                <span>{chip.label}</span>
                <svg width="9" height="9" viewBox="0 0 10 10" fill="none" aria-hidden="true">
                  <circle cx="5" cy="5" r="3.25" stroke={C.accent} strokeWidth="1.15" />
                  <circle cx="5" cy="5" r="1" fill={C.accent} />
                </svg>
              </button>
            ))}
            <button
              onClick={resetFilters}
              title="Clear all filters and return to the full live tape"
              style={{
                marginLeft: "auto",
                border: `1px solid ${C.lineStrong}`,
                background: "transparent",
                color: C.text,
                borderRadius: 4,
                padding: "4px 10px",
                cursor: "pointer",
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: 1,
                textTransform: "uppercase",
              }}
            >
              Reset
            </button>
            <button
              onClick={() => setFilterSummaryOpen(false)}
              aria-label="Close filtered tape summary"
              style={{
                border: `1px solid ${C.lineStrong}`,
                background: "transparent",
                color: C.text,
                borderRadius: 4,
                width: 26,
                height: 26,
                cursor: "pointer",
                fontSize: 18,
                lineHeight: 1,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              ×
            </button>
          </div>
        )}

        {/* Control bar — three zones that resize/wrap with the screen:
            [Filters · Columns · Sort · Rows]  [‹ Prev · PAGE · Next ›]  [Search · LIVE · CSV] */}
        <div
          className="flow-toolbar"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "14px 20px",
            borderBottom: `1px solid ${C.line}`,
            flexWrap: "wrap",
            rowGap: 8,
          }}
        >
          {/* ── Zone 1: view controls ── */}
          <div className="flow-zone1" style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", rowGap: 8 }}>
          <button
            className="flow-btn"
            onClick={() => setSidebarOpen((o) => !o)}
            style={{
              ...btn,
              border: `1px solid ${activeCount > 0 ? C.accent : C.lineStrong}`,
              background: activeCount > 0 ? "rgba(16,185,129,0.06)" : btn.background,
            }}
          >
            Filters
            {activeCount > 0 && (
              <span
                style={{
                  border: `1px solid ${C.accent}`,
                  color: C.accent,
                  borderRadius: 999,
                  fontSize: 10,
                  fontWeight: 700,
                  padding: "0 6px",
                }}
              >
                {activeCount}
              </span>
            )}
          </button>

          {/* Column selector */}
          <div style={{ position: "relative" }} data-flow-menu>
            <button className="flow-btn" onClick={() => setOpenMenu((m) => (m === "cols" ? null : "cols"))} style={btn}>
              Columns
            </button>
            {openMenu === "cols" && (
              <div
                style={{
                  ...menuPanel,
                  padding: 12,
                  width: 230,
                  maxHeight: 380,
                  overflowY: "auto",
                }}
              >
                {/* Select all / defaults / none */}
                <div
                  style={{
                    display: "flex",
                    gap: 6,
                    paddingBottom: 9,
                    marginBottom: 7,
                    borderBottom: `1px solid ${C.line}`,
                  }}
                >
                  {(
                    [
                      { label: "All", act: () => setAllCols("all") },
                      { label: "Defaults", act: () => setAllCols("defaults") },
                      { label: "None", act: () => setAllCols("none") },
                    ] as const
                  ).map((b) => (
                    <button
                      key={b.label}
                      onClick={b.act}
                      style={{
                        flex: 1,
                        background: "transparent",
                        border: `1px solid rgba(255,255,255,0.28)`,
                        borderRadius: 4,
                        color: C.text,
                        cursor: "pointer",
                        fontSize: 10,
                        fontWeight: 600,
                        letterSpacing: 0.6,
                        textTransform: "uppercase",
                        padding: "5px 0",
                      }}
                    >
                      {b.label}
                    </button>
                  ))}
                </div>
                {ALL_COLUMNS.map((c) => {
                  const on = visibleCols.has(c.key);
                  return (
                    <button
                      key={c.key}
                      onClick={() => toggleCol(c.key)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        width: "100%",
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        padding: "6px 4px",
                        color: on ? C.text : C.textDim,
                        fontSize: 12,
                        textAlign: "left",
                      }}
                    >
                      <span
                        style={{
                          width: 12,
                          height: 12,
                          border: `1px solid ${on ? C.accent : C.line}`,
                          background: on ? C.accent : "transparent",
                          flexShrink: 0,
                        }}
                      />
                      {c.label}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Sort dropdown — mirrors UW's toolbar Sort control. */}
          <div style={{ position: "relative" }} data-flow-menu>
            <button className="flow-btn" onClick={() => setOpenMenu((m) => (m === "sort" ? null : "sort"))} style={btn} title="Change how the tape is sorted">
              <span style={{ color: C.textDim, fontSize: 11 }}>Sort:</span>
              <span style={{ color: C.accent, fontWeight: 600 }}>
                {SORT_OPTIONS.find((s) => s.v === orderBy)?.label || "Time"}
              </span>
              <svg width="9" height="9" viewBox="0 0 10 10">
                <path d={orderDir === "asc" ? "M5 1 L9 8 L1 8 Z" : "M5 9 L1 2 L9 2 Z"} fill={C.accent} />
              </svg>
            </button>
            {openMenu === "sort" && (
              <div
                style={{
                  ...menuPanel,
                  width: 190,
                  maxHeight: 360,
                  overflowY: "auto",
                }}
              >
                {SORT_OPTIONS.map((s) => {
                  const active = s.v === orderBy;
                  return (
                    <button
                      key={s.v}
                      onClick={() => {
                        setSort(s.v);
                        setOpenMenu(null);
                      }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        width: "100%",
                        background: active ? "rgba(255,255,255,0.05)" : "none",
                        border: "none",
                        cursor: "pointer",
                        padding: "7px 8px",
                        color: active ? C.accent : C.text,
                        fontSize: 12,
                        textAlign: "left",
                        borderRadius: 3,
                      }}
                    >
                      {s.label}
                      {active && <span style={{ fontSize: 10 }}>{orderDir === "asc" ? "▲ asc" : "▼ desc"}</span>}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Results-per-page — custom dropdown, styled like the Sort menu. */}
          <div style={{ position: "relative" }} data-flow-menu>
            <button className="flow-btn" onClick={() => setOpenMenu((m) => (m === "rows" ? null : "rows"))} style={btn} title="How many rows to load">
              <span style={{ color: C.accent, fontWeight: 600 }}>{Number(applied.limit) || 100}</span>
              <span style={{ color: C.textDim, fontSize: 11 }}>rows</span>
              <svg width="9" height="9" viewBox="0 0 10 10">
                <path d="M5 9 L1 2 L9 2 Z" fill={C.accent} />
              </svg>
            </button>
            {openMenu === "rows" && (
              <div
                style={{
                  ...menuPanel,
                  width: 120,
                }}
              >
                {RESULT_COUNTS.map((n) => {
                  const active = (Number(applied.limit) || 100) === n;
                  return (
                    <button
                      key={n}
                      onClick={() => {
                        setOpenMenu(null);
                        const next: FlowParams = { ...appliedRef.current, limit: String(n) };
                        if (n === 100) delete next.limit;
                        setApplied(next);
                        setDraft((d) => {
                          const nd = { ...d };
                          if (n === 100) delete nd.limit;
                          else nd.limit = String(n);
                          return nd;
                        });
                        syncUrl(next);
                        fetchRows(next);
                      }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        width: "100%",
                        background: active ? "rgba(255,255,255,0.05)" : "none",
                        border: "none",
                        cursor: "pointer",
                        padding: "7px 8px",
                        color: active ? C.accent : C.text,
                        fontSize: 12,
                        textAlign: "left",
                        borderRadius: 3,
                      }}
                    >
                      {n} rows
                      {active && <span style={{ fontSize: 10 }}>✓</span>}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          </div>
          {/* ── end Zone 1 ── */}

          {/* ── Zone 2 (center): Prev · PAGE · Next as one segmented control ── */}
          <div
            className="flow-zone2"
            style={{
              display: "flex",
              alignItems: "stretch",
              marginInline: "auto",
              border: `1px solid ${C.lineStrong}`,
              borderRadius: 4,
              overflow: "hidden",
            }}
          >
            <button
              className="flow-page-btn"
              onClick={pagePrev}
              disabled={pageCursors.length === 0}
              title="Newer trades (back toward live)"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                background: "rgba(255,255,255,0.03)",
                border: "none",
                borderRight: `1px solid ${C.lineStrong}`,
                color: pageCursors.length ? C.text : "rgba(255,255,255,0.35)",
                padding: "7px 13px",
                fontSize: 12,
                letterSpacing: 0.5,
                cursor: pageCursors.length ? "pointer" : "default",
                whiteSpace: "nowrap",
              }}
            >
              ‹ Prev
            </button>
            <span
              style={{
                display: "flex",
                alignItems: "center",
                padding: "0 12px",
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: 1,
                color: pageCursors.length ? C.gold : C.text,
                background: "rgba(255,255,255,0.015)",
                whiteSpace: "nowrap",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              PAGE {pageCursors.length + 1}
            </span>
            <button
              className="flow-page-btn"
              onClick={pageNext}
              disabled={!hasMorePages}
              title={hasMorePages ? "Older trades (page back through history)" : "No more history for these filters"}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                background: "rgba(255,255,255,0.03)",
                border: "none",
                borderLeft: `1px solid ${C.lineStrong}`,
                color: hasMorePages ? C.text : "rgba(255,255,255,0.35)",
                padding: "7px 13px",
                fontSize: 12,
                letterSpacing: 0.5,
                cursor: hasMorePages ? "pointer" : "default",
                whiteSpace: "nowrap",
              }}
            >
              Next ›
            </button>
          </div>

          {/* ── Zone 3 (right): search (auto-applies) · LIVE · CSV ── */}
          <div className="flow-zone3" style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", rowGap: 8 }}>
          {/* Ticker quick-search — fires on its own: debounced as you type, or
              Enter for instant. No Apply button needed. */}
          <div style={{ position: "relative", display: "flex", alignItems: "center", flex: "1 1 150px", minWidth: 130, maxWidth: 280 }}>
            <input
              className="flow-ticker-input"
              value={draft.ticker || ""}
              onChange={(e) => setDraftParam("ticker", e.target.value.toUpperCase() || null)}
              onKeyDown={(e) => e.key === "Enter" && applyTickerNow()}
              placeholder="Tickers (AAPL, TSLA…)"
              style={{
                width: "100%",
                background: "rgba(255,255,255,0.03)",
                border: `1px solid ${(draft.ticker || "") !== (applied.ticker || "") ? C.accent : C.lineStrong}`,
                borderRadius: 4,
                color: C.text,
                padding: "7px 30px 7px 12px",
                fontSize: 12,
                letterSpacing: 1,
                outline: "none",
                transition: "border-color 160ms ease",
              }}
            />
            {(draft.ticker || "") !== (applied.ticker || "") && (
              <span
                title="Loading…"
                style={{
                  position: "absolute",
                  right: 10,
                  width: 6,
                  height: 6,
                  borderRadius: 999,
                  background: C.accent,
                  boxShadow: `0 0 8px ${C.accent}`,
                }}
              />
            )}
          </div>

          {/* LIVE / PAUSED — a real, obviously-clickable button. LIVE is
              emerald with a pulsing dot; PAUSED goes amber with a big play
              glyph so it clearly says "click me to resume", never "disabled". */}
          <button
            onClick={toggleLive}
            title={live ? "Click to pause the live tape" : "Click to resume the live tape"}
            className="flow-live-btn flow-btn"
            style={{
              ...btn,
              border: `1px solid ${live ? C.accent : C.gold}`,
              background: live ? "rgba(16,185,129,0.14)" : "rgba(231,198,107,0.12)",
              color: live ? C.accent : C.gold,
              fontWeight: 700,
              letterSpacing: 1,
              boxShadow: live && streamState === "live" ? `0 0 14px ${C.accentDim}` : "0 0 12px rgba(231,198,107,0.08)",
              transition: "all 160ms ease",
            }}
          >
            {live ? (
              <span
                style={{
                  width: 7,
                  height: 7,
                  borderRadius: 999,
                  background: streamState === "live" ? C.accent : C.textDim,
                  boxShadow: streamState === "live" ? `0 0 10px ${C.accent}` : "none",
                }}
              />
            ) : (
              // Play glyph is the hero when paused — unmissable resume affordance.
              <svg width="11" height="11" viewBox="0 0 12 12" style={{ flexShrink: 0 }}>
                <path d="M2.5 1 L11 6 L2.5 11 Z" fill={C.gold} />
              </svg>
            )}
            {live ? "LIVE" : "PAUSED"}
            {live && (
              <svg width="10" height="10" viewBox="0 0 12 12" style={{ flexShrink: 0 }}>
                <rect x="2" y="1.5" width="3" height="9" rx="1" fill={C.accent} />
                <rect x="7" y="1.5" width="3" height="9" rx="1" fill={C.accent} />
              </svg>
            )}
          </button>

          {/* CSV export of the visible rows. */}
          <button className="flow-btn" onClick={exportCsv} title="Download the visible rows as CSV" style={{ ...btn, padding: "7px 12px" }}>
            CSV
          </button>

          {!live && buffer.length > 0 && (
            <button
              className="flow-btn"
              onClick={toggleLive}
              style={{
                ...btn,
                border: `1px solid ${C.accent}`,
                color: C.accent,
                fontWeight: 600,
              }}
            >
              {buffer.length} new ↑
            </button>
          )}
          </div>
          {/* ── end Zone 3 ── */}
        </div>

        {/* Table — memoized: keystrokes in filter inputs never re-render it. */}
        <FlowTable
          columns={columns}
          rows={displayRows}
          loading={loading}
          error={error}
          pulseCols={pulseCols}
          orderBy={orderBy}
          orderDir={orderDir}
          onSort={setSort}
          maxPrem={maxPrem}
        />

        {/* Footer status */}
        <div
          className="flow-footer"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 18,
            padding: "9px 20px",
            borderTop: `1px solid ${C.line}`,
            fontSize: 11,
            letterSpacing: 0.6,
            color: C.textDim,
          }}
        >
          <span>{displayRows.length} ROWS</span>
          <span>
            STREAM{" "}
            <span style={{ color: streamState === "live" ? C.accent : C.gold }}>
              {streamState === "live" ? "CONNECTED" : streamState === "connecting" ? "CONNECTING…" : "RECONNECTING…"}
            </span>
          </span>
          {dirty && (
            <span style={{ color: C.accent }}>UNAPPLIED FILTER CHANGES — HIT APPLY IN THE FILTERS PANEL</span>
          )}
          <span style={{ marginLeft: "auto" }}>XYNTH · FREE REAL-TIME OPTIONS FLOW</span>
        </div>
      </div>
      </div>
      </div>
    </div>
  );
}
